import {
  ref,
  onMounted,
  onUnmounted,
  provide,
  type Ref,
  type InjectionKey,
} from "vue";

import { checkValuesAlreadyProvided, injectRequiredWithSelf } from "@mcwd/util-vue";

import { useMarketoFormInternals } from "./use-marketo-form-instance.js";
import { useFormEvents } from "./use-form-events.js";
import { useFormSettings } from "./use-form-settings.js";
import { isNil, isString, type Nil } from "@mcwd/typescript-type-guards";

import type { MktoFormObj, MktoFormObjWithUndocumentedExtras } from "../types/Marketo.js";

import type {} from "../types/recaptcha.js";
import { loadScriptAsPromise } from "../../utils/loadScriptAsPromise.js";
import { WaitForElementAsPromise } from "../../utils/wait-for-element-as-promise.js";
import { useFormLogger } from "./use-form-logger.js";
import type { ILogger } from "../../utils/Logger/ILogger.js";



interface UseMarketoRecaptchaInjectionValues {
  setRecaptchaToken: () => Promise<void>
}

type UseMarketoRecaptchaInjectionKeys = {
  [key in keyof UseMarketoRecaptchaInjectionValues]: InjectionKey<
    UseMarketoRecaptchaInjectionValues[key]
  >;
};

const UseMarketoRecaptchaInjectionKeys: UseMarketoRecaptchaInjectionKeys = {
  setRecaptchaToken: Symbol("setRecaptchaToken"),
};

function initSetupRecaptcha() {
  const { logger } = useFormLogger();
  let loadRecaptchaPromise: Promise<void> | Nil = null;
  // Load Recaptcha
  return (siteKey: string, scriptTagRef: Ref<HTMLScriptElement | Nil>) => {
    loadRecaptchaPromise ??= new Promise<void>((resolve, reject) => {
      loadScriptAsPromise("https://www.google.com/recaptcha/api.js?render=" + siteKey)
        .then((scriptTag: HTMLScriptElement) => {
          scriptTagRef.value = scriptTag;
          window.grecaptcha.ready(() => {
            resolve();
          });
        })
        .catch((event) => reject(event));
    }).catch(logger.error);
    return loadRecaptchaPromise;
  };
}

function setMarketoCaptchaToken(token: string, marketoFormObj: MktoFormObjWithUndocumentedExtras, formEl: JQuery<HTMLFormElement>, logger: ILogger) {
  logger.debug("setMarketoCaptchaToken");
  // try to set token using Marketo code and then setting it on error
  try {
    marketoFormObj.setCaptchaValue(token);
    return Promise.resolve(token);
  }
  catch {
    marketoFormObj.addHiddenFields({
      captchaToken: token,
    });
    return WaitForElementAsPromise<HTMLInputElement>("*[name=captchaToken]", formEl.get(0))
      .then(tokenField => {
        const val = $(tokenField).val();
        if (isString(val) && val.trim().length > 0) {
          return val;
        }
        else {
          throw new Error("recaptcha input not found");
        }
      })
      .catch(err => {
        logger.error(err);
        throw new Error("recaptcha input not found");
      });
  }
}

async function grecaptchaExecSubmit(siteKey: string, marketoFormObj: MktoFormObj, formEl: JQuery<HTMLFormElement>, logger: ILogger): Promise<string>  {
  logger.debug("grecaptchaExecSubmit");
  return await window.grecaptcha
    .execute(siteKey, { action: "submit" })
    .then((token: string) => {
      return setMarketoCaptchaToken(token, marketoFormObj as MktoFormObjWithUndocumentedExtras, formEl, logger);
    });
}

function initExecReCaptcha(setupRecaptcha: ReturnType<typeof initSetupRecaptcha>, logger: ILogger) {
  return async (siteKey: string, marketoFormObj: MktoFormObj, formEl: JQuery<HTMLFormElement>, scriptTagRef: Ref<HTMLScriptElement | Nil>) => {
    await setupRecaptcha(siteKey, scriptTagRef);
    return new Promise<void>((resolve, _reject) => {
      logger.debug("setupRecaptcha");
      window.grecaptcha.ready(() => {
        grecaptchaExecSubmit(siteKey, marketoFormObj, formEl, logger)
          .then(token => {
            if (isString(token) && token.length > 0) {
              resolve();
            }
          })
          .catch((err: unknown)=> {
            err = err ?? "Error in grecaptchaExecSubmit";
            logger.error(err);
            throw err;
          });
      });
    });
  };
}

export function initMarketoRecaptcha() {
  if (checkValuesAlreadyProvided(UseMarketoRecaptchaInjectionKeys)) {
    return;
  }

  const { logger, getVerboseLogger } = useFormLogger({ callerLabel: "initMarketoFormRecaptcha" });
  const verboseLogger = getVerboseLogger("initMarketoRecaptcha");
  verboseLogger.debug("START initMarketoRecaptcha");
  // Load Recaptcha
  const setupRecaptcha = initSetupRecaptcha();

  // load and use reCAPTCHA
  const execReCaptcha = initExecReCaptcha(setupRecaptcha, logger);

  const scriptTagRef: Ref<HTMLScriptElement | Nil> = ref(null);

  const { MarketoFormObj, MarketoFormHtmlElement } = useMarketoFormInternals({ callerLabel: "iniMarketoRecaptcha" });
  const { FormSettings } = useFormSettings({ callerLabel: "iniMarketoRecaptcha" });

  const setRecaptchaToken = async () => {
    if (isNil(FormSettings)) {
      throw new Error("FormSettings is null");
    }
    if (isNil(MarketoFormObj.value)) {
      throw new Error("MarketoFormObj is null.");
    }
    if (isNil(MarketoFormHtmlElement.value)) {
      throw new Error("MarketoFormHtmlElement is null.");
    }
    return await execReCaptcha(
      FormSettings.recaptchaSiteKey,
      MarketoFormObj.value,
      MarketoFormHtmlElement.value,
      scriptTagRef
    );
  };


  verboseLogger.debug("PROVIDE setRecaptchaToken");
  provide(UseMarketoRecaptchaInjectionKeys.setRecaptchaToken, setRecaptchaToken);

  const events = useFormEvents({ callerLabel: "iniMarketoRecaptcha" });


  onMounted(() => {
    events.marketoSetup.on(() => {
      setupRecaptcha(FormSettings.recaptchaSiteKey, scriptTagRef).catch(
        logger.error
      );
    });
  });

  onUnmounted(() => {
    if (scriptTagRef.value) {
      const parent = scriptTagRef.value?.parentElement;
      if (parent) {
        parent.removeChild(scriptTagRef.value);
        scriptTagRef.value = null;
      }
    }
  });

  verboseLogger.debug("END initMarketoRecaptcha");
}

export function useMarketoRecaptcha({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger({ callerLabel: "useMarketoRecaptcha" });
  try {
    callerLabel ??= "useMarketoRecaptcha";
    const setRecaptchaToken = injectRequiredWithSelf(UseMarketoRecaptchaInjectionKeys.setRecaptchaToken);
    return { setRecaptchaToken };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}